import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Box, Button, Typography, Paper, Chip, Avatar, Tooltip } from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PauseIcon from '@mui/icons-material/Pause';
import CallEndIcon from '@mui/icons-material/CallEnd';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import CloseIcon from '@mui/icons-material/Close';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';

/**
 * IncomingCallNotification Component - Redesigned
 * 
 * Clean, modern notification for incoming calls with improved visual hierarchy.
 * Features real-time waiting counter, professional design, and clear action buttons.
 * 
 * Design System:
 * - Primary Blue: #0066CC (professional, trustworthy)
 * - Success Green: #10B981 (accept actions)
 * - Warning Amber: #F59E0B (hold actions)
 * - Danger Red: #EF4444 (decline actions)
 * - Neutral Gray: #6B7280 (secondary actions)
 * 
 * @param {Object} props
 * @param {Array} props.incomingCalls - Array of incoming call objects
 * @param {Boolean} props.hasActiveConsultation - Whether there's an active consultation
 * @param {Function} props.onAccept - Callback to accept call (when no active consultation)
 * @param {Function} props.onAcceptAndEnd - Callback to accept new call and end current
 * @param {Function} props.onAcceptAndHold - Callback to accept new call and hold current
 * @param {Function} props.onSilence - Callback when silence button is clicked
 * @param {Function} props.onDecline - Callback when decline button is clicked
 */
const IncomingCallNotification = ({ 
  incomingCalls, 
  hasActiveConsultation = false,
  onAccept,
  onAcceptAndEnd,
  onAcceptAndHold,
  onSilence,
  onDecline
}) => {
  const [currentTime, setCurrentTime] = useState(Date.now());

  // Update time every second for real-time waiting counter
  useEffect(() => {
    if (!incomingCalls || incomingCalls.length === 0) {
      return;
    }

    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, [incomingCalls]);

  if (!incomingCalls || incomingCalls.length === 0) {
    return null;
  }

  // Show only the most recent incoming call (first in array)
  const latestCall = incomingCalls[0];
  
  const formatWaitingTime = (timestamp) => {
    const seconds = Math.floor((currentTime - timestamp) / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const waitingTime = formatWaitingTime(latestCall.timestamp);

  // Design tokens
  const tokens = {
    primary: '#0066CC',
    primaryLight: '#E6F0FA',
    primaryDark: '#004A99',
    success: '#10B981',
    successLight: '#D1FAE5',
    successDark: '#059669',
    warning: '#F59E0B',
    warningLight: '#FEF3C7',
    warningDark: '#D97706',
    danger: '#EF4444',
    dangerLight: '#FEE2E2',
    dangerDark: '#DC2626',
    neutral: '#6B7280',
    neutralLight: '#F3F4F6',
    neutralDark: '#4B5563',
    text: {
      primary: '#111827',
      secondary: '#6B7280',
      white: '#FFFFFF',
    },
    border: '#E5E7EB',
    background: '#FFFFFF',
    shadow: '0 4px 24px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
    radius: '12px',
  };

  // Use Portal to render outside Dialog's stacking context
  const notificationContent = (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999999999,
        pointerEvents: 'none',
      }}
    >
      <Paper
        elevation={0}
        sx={{
          position: 'fixed',
          top: 20,
          right: 20,
          width: 340,
          background: tokens.background,
          border: `1px solid ${tokens.border}`,
          borderRadius: tokens.radius,
          padding: 0,
          boxShadow: tokens.shadow,
          pointerEvents: 'auto',
          overflow: 'hidden',
          animation: 'slideInRight 0.3s ease-out',
          '@keyframes slideInRight': {
            from: {
              transform: 'translateX(400px)',
              opacity: 0,
            },
            to: {
              transform: 'translateX(0)',
              opacity: 1,
            },
          },
        }}
      >
        {/* Header - Clean and minimal */}
        <Box
          sx={{
            background: tokens.primaryLight,
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            borderBottom: `1px solid ${tokens.border}`,
          }}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '8px',
              background: tokens.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              animation: 'pulse 2s infinite',
              '@keyframes pulse': {
                '0%, 100%': { transform: 'scale(1)', opacity: 1 },
                '50%': { transform: 'scale(1.05)', opacity: 0.9 },
              },
            }}
          >
            <PhoneInTalkIcon sx={{ fontSize: 18, color: tokens.text.white }} />
          </Box>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '14px',
              color: tokens.primary,
              flex: 1,
              letterSpacing: '-0.01em',
            }}
          >
            Incoming Call
          </Typography>
          {incomingCalls.length > 1 && (
            <Chip
              label={`+${incomingCalls.length - 1} more`}
              size="small"
              sx={{
                height: 24,
                fontSize: '12px',
                fontWeight: 600,
                background: tokens.primary,
                color: tokens.text.white,
                '& .MuiChip-label': { px: 1 },
              }}
            />
          )}
        </Box>

        {/* Patient Information */}
        <Box sx={{ padding: '16px' }}>
          {/* Patient Row */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Avatar
              sx={{
                width: 48,
                height: 48,
                background: `linear-gradient(135deg, ${tokens.primary} 0%, ${tokens.primaryDark} 100%)`,
                fontWeight: 600,
                fontSize: '16px',
                border: `2px solid ${tokens.primaryLight}`,
              }}
            >
              {latestCall.patientName
                ? latestCall.patientName
                    .split(' ')
                    .map((n) => n[0])
                    .join('')
                    .toUpperCase()
                    .substring(0, 2)
                : '??'}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  color: tokens.text.primary,
                  fontSize: '16px',
                  lineHeight: 1.3,
                  mb: 0.5,
                }}
                noWrap
              >
                {latestCall.patientName || 'Unknown Patient'}
              </Typography>
              <Chip
                label={latestCall.pucode.toUpperCase()}
                size="small"
                sx={{
                  height: 22,
                  fontSize: '11px',
                  fontWeight: 600,
                  background: tokens.neutralLight,
                  color: tokens.text.secondary,
                  border: `1px solid ${tokens.border}`,
                  '& .MuiChip-label': { px: 1 },
                }}
              />
            </Box>
            {/* Waiting Time - Prominent */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '8px 12px',
                background: tokens.warningLight,
                borderRadius: '8px',
                border: `1px solid ${tokens.warning}20`,
              }}
            >
              <Typography
                sx={{
                  fontSize: '18px',
                  fontWeight: 700,
                  color: tokens.warningDark,
                  lineHeight: 1,
                }}
              >
                {waitingTime}
              </Typography>
              <Typography
                sx={{
                  fontSize: '10px',
                  fontWeight: 500,
                  color: tokens.warning,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Waiting
              </Typography>
            </Box>
          </Box>

          {/* Location Bar */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              padding: '10px 12px',
              background: tokens.neutralLight,
              borderRadius: '8px',
              mb: 2,
            }}
          >
            <LocationOnIcon sx={{ fontSize: 16, color: tokens.neutral }} />
            <Typography
              sx={{
                color: tokens.text.secondary,
                fontSize: '13px',
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {latestCall.location || 'Unknown Location'}
            </Typography>
          </Box>

          {/* Action Buttons */}
          {hasActiveConsultation ? (
            // Active consultation: Show End & Accept, Hold & Accept
            <React.Fragment>
              {/* Primary Actions Row */}
              <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
                <Tooltip title="End current call and accept this one" arrow placement="top">
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<CallEndIcon sx={{ fontSize: 18 }} />}
                    onClick={() => {
                      console.log(`📞 [Call Waiting] End & Accept clicked`, { pucode: latestCall.pucode });
                      if (onAcceptAndEnd) onAcceptAndEnd(latestCall);
                    }}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '13px',
                      padding: '10px 16px',
                      borderRadius: '8px',
                      background: tokens.success,
                      boxShadow: 'none',
                      minHeight: 44,
                      '&:hover': {
                        background: tokens.successDark,
                        boxShadow: `0 4px 12px ${tokens.success}40`,
                      },
                    }}
                  >
                    End & Accept
                  </Button>
                </Tooltip>
                <Tooltip title="Put current call on hold and accept this one" arrow placement="top">
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<PauseIcon sx={{ fontSize: 18 }} />}
                    onClick={() => {
                      console.log(`📞 [Call Waiting] Hold & Accept clicked`, { pucode: latestCall.pucode });
                      if (onAcceptAndHold) onAcceptAndHold(latestCall);
                    }}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '13px',
                      padding: '10px 16px',
                      borderRadius: '8px',
                      background: tokens.warning,
                      boxShadow: 'none',
                      minHeight: 44,
                      '&:hover': {
                        background: tokens.warningDark,
                        boxShadow: `0 4px 12px ${tokens.warning}40`,
                      },
                    }}
                  >
                    Hold & Accept
                  </Button>
                </Tooltip>
              </Box>
              {/* Secondary Actions Row */}
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<VolumeOffIcon sx={{ fontSize: 16 }} />}
                  onClick={() => {
                    console.log(`🔇 [Call Waiting] Silence clicked`, { pucode: latestCall.pucode });
                    if (onSilence) onSilence(latestCall.pucode);
                  }}
                  disabled={latestCall.isSilenced}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '13px',
                    padding: '8px 16px',
                    borderRadius: '8px',
                    minHeight: 40,
                    borderColor: tokens.border,
                    color: latestCall.isSilenced ? tokens.neutral : tokens.text.secondary,
                    background: latestCall.isSilenced ? tokens.neutralLight : 'transparent',
                    '&:hover': {
                      borderColor: tokens.neutral,
                      background: tokens.neutralLight,
                    },
                    '&.Mui-disabled': {
                      borderColor: tokens.border,
                      color: tokens.neutral,
                    },
                  }}
                >
                  {latestCall.isSilenced ? 'Silenced' : 'Silence'}
                </Button>
                <Tooltip title="Decline this call (other pharmacists can still accept)" arrow placement="top">
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<CloseIcon sx={{ fontSize: 16 }} />}
                    onClick={() => {
                      console.log(`❌ [Call Waiting] Decline clicked`, { pucode: latestCall.pucode });
                      if (onDecline) onDecline(latestCall);
                    }}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '13px',
                      padding: '8px 16px',
                      borderRadius: '8px',
                      minHeight: 40,
                      borderColor: tokens.danger,
                      color: tokens.danger,
                      '&:hover': {
                        borderColor: tokens.dangerDark,
                        background: tokens.dangerLight,
                      },
                    }}
                  >
                    Decline
                  </Button>
                </Tooltip>
              </Box>
            </React.Fragment>
          ) : (
            // No active consultation: Show Accept, Silence, Decline
            <React.Fragment>
              {/* Primary Accept Button */}
              <Box sx={{ mb: 1.5 }}>
                <Tooltip title="Accept this call" arrow placement="top">
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<PhoneIcon sx={{ fontSize: 18 }} />}
                    onClick={() => {
                      console.log(`📞 [Call Waiting] Accept clicked`, { pucode: latestCall.pucode });
                      if (onAccept) onAccept(latestCall);
                    }}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '14px',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      background: tokens.success,
                      boxShadow: 'none',
                      minHeight: 48,
                      '&:hover': {
                        background: tokens.successDark,
                        boxShadow: `0 4px 12px ${tokens.success}40`,
                      },
                    }}
                  >
                    Accept Call
                  </Button>
                </Tooltip>
              </Box>
              {/* Secondary Actions Row */}
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<VolumeOffIcon sx={{ fontSize: 16 }} />}
                  onClick={() => {
                    console.log(`🔇 [Call Waiting] Silence clicked`, { pucode: latestCall.pucode });
                    if (onSilence) onSilence(latestCall.pucode);
                  }}
                  disabled={latestCall.isSilenced}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '13px',
                    padding: '8px 16px',
                    borderRadius: '8px',
                    minHeight: 40,
                    borderColor: tokens.border,
                    color: latestCall.isSilenced ? tokens.neutral : tokens.text.secondary,
                    background: latestCall.isSilenced ? tokens.neutralLight : 'transparent',
                    '&:hover': {
                      borderColor: tokens.neutral,
                      background: tokens.neutralLight,
                    },
                    '&.Mui-disabled': {
                      borderColor: tokens.border,
                      color: tokens.neutral,
                    },
                  }}
                >
                  {latestCall.isSilenced ? 'Silenced' : 'Silence'}
                </Button>
                <Tooltip title="Decline this call (other pharmacists can still accept)" arrow placement="top">
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<CloseIcon sx={{ fontSize: 16 }} />}
                    onClick={() => {
                      console.log(`❌ [Call Waiting] Decline clicked`, { pucode: latestCall.pucode });
                      if (onDecline) onDecline(latestCall);
                    }}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '13px',
                      padding: '8px 16px',
                      borderRadius: '8px',
                      minHeight: 40,
                      borderColor: tokens.danger,
                      color: tokens.danger,
                      '&:hover': {
                        borderColor: tokens.dangerDark,
                        background: tokens.dangerLight,
                      },
                    }}
                  >
                    Decline
                  </Button>
                </Tooltip>
              </Box>
            </React.Fragment>
          )}
        </Box>
      </Paper>
    </div>
  );

  // Render using Portal to ensure it's above Dialog
  return ReactDOM.createPortal(notificationContent, document.body);
};

export default IncomingCallNotification;



// WEBPACK FOOTER //
// ./src/components/home/IncomingCallNotification.js